import type { AuthClient } from '../auth/client';
import type { AuthSession } from '../auth/types';
import { el } from '../ui/dom';
import { loadLang } from '../scenes/hub/hubCopy';
import whiteLogoSheet from '../assets/landing/academy-white-logo-sheet.png';
import {
  createAuditView,
  createSchoolsView,
  createSystemAdminsView,
} from './adminViews';
import { CmsClient, type CmsSchool } from './client';
import { createDashboardView } from './dashboardView';
import { createStudentsView, createTeachersView } from './peopleViews';
import { emptyState, errorMessage, icon, loadingState, setSelectOptions } from './ui';
import './teacherStyles.css';

type CmsViewId = 'dashboard' | 'students' | 'teachers' | 'schools' | 'admins' | 'audit';

interface NavItem {
  readonly id: CmsViewId;
  readonly label: string;
}

export async function showTeacherCms(
  root: HTMLElement,
  session: AuthSession,
  auth: AuthClient
): Promise<void> {
  const client = new CmsClient();
  const isAdmin = session.user.role === 'admin';
  const navItems: readonly NavItem[] = isAdmin
    ? [
        { id: 'dashboard', label: 'Dashboard' },
        { id: 'students', label: 'Students' },
        { id: 'teachers', label: 'Teachers' },
        { id: 'schools', label: 'Schools' },
        { id: 'admins', label: 'System Admins' },
        { id: 'audit', label: 'Audit' },
      ]
    : [
        { id: 'dashboard', label: 'Dashboard' },
        { id: 'students', label: 'Students' },
        { id: 'audit', label: 'Audit' },
      ];
  let schools: readonly CmsSchool[] = [];
  let schoolId = session.user.schoolId;
  let activeView: CmsViewId = 'dashboard';

  const status = el('p', {
    class: 'cms-global-status',
    attrs: { role: 'status', 'aria-live': 'polite' },
  });
  const viewRoot = el('main', { class: 'cms-workspace' });
  const schoolSelect = el('select', {
    class: 'cms-school-select',
    attrs: { 'aria-label': 'Selected school' },
  });
  const nav = el('nav', { class: 'cms-nav', attrs: { 'aria-label': 'CMS sections' } });

  const playGames = (): void => {
    const url = new URL(window.location.href);
    url.searchParams.set('play', '1');
    url.hash = '/welcome';
    window.location.assign(url.toString());
  };

  const announce = (message: string, error = false): void => {
    status.textContent = message;
    status.classList.toggle('cms-global-status--error', error);
  };

  const updateSchoolSelect = (): void => {
    if (!isAdmin) return;
    const activeSchools = schools.filter((school) => school.status === 'active');
    if (!activeSchools.some((school) => school.id === schoolId)) schoolId = activeSchools[0]?.id ?? '';
    setSelectOptions(
      schoolSelect,
      activeSchools.length
        ? activeSchools.map((school) => ({ value: school.id, label: `${school.code} · ${school.name}` }))
        : [{ value: '', label: 'No active schools' }],
      schoolId
    );
  };

  const renderView = async (): Promise<void> => {
    nav.querySelectorAll<HTMLButtonElement>('button').forEach((item) => {
      item.classList.toggle('cms-nav__item--active', item.dataset.view === activeView);
      item.setAttribute('aria-current', item.dataset.view === activeView ? 'page' : 'false');
    });
    window.history.replaceState(null, '', `#/cms/${activeView}`);
    schoolSelect.hidden = activeView === 'admins' || activeView === 'audit';
    viewRoot.replaceChildren(loadingState(`Loading ${activeView}…`));
    if (
      !schoolId &&
      activeView !== 'schools' &&
      !(isAdmin && (activeView === 'admins' || activeView === 'audit'))
    ) {
      viewRoot.replaceChildren(
        emptyState(
          'Create an active school first.',
          'School tenants are explicit. Add a school and an allowed email domain before importing accounts.'
        )
      );
      return;
    }
    try {
      let view: HTMLElement;
      switch (activeView) {
        case 'students':
          view = await createStudentsView(client, schoolId, announce, isAdmin);
          break;
        case 'teachers':
          view = await createTeachersView(client, schoolId, schools, announce);
          break;
        case 'schools':
          view = await createSchoolsView(
            client,
            (nextSchools) => {
              schools = nextSchools;
              updateSchoolSelect();
            },
            announce
          );
          break;
        case 'admins':
          view = await createSystemAdminsView(client, session.user.id, announce);
          break;
        case 'audit':
          view = await createAuditView(client, isAdmin ? '' : schoolId, announce);
          break;
        default:
          view = await createDashboardView(client, schoolId, announce);
      }
      viewRoot.replaceChildren(view);
    } catch (reason) {
      announce(errorMessage(reason), true);
      viewRoot.replaceChildren(emptyState('This CMS view could not load.', errorMessage(reason)));
    }
  };

  nav.replaceChildren(
    ...navItems.map((item) =>
      el(
        'button',
        {
          class: 'cms-nav__item',
          attrs: { type: 'button', 'data-view': item.id },
          on: {
            click: () => {
              activeView = item.id;
              void renderView();
            },
          },
        },
        icon(item.id),
        el('span', { text: item.label })
      )
    )
  );
  schoolSelect.addEventListener('change', () => {
    schoolId = schoolSelect.value;
    void renderView();
  });

  const shell = el(
    'div',
    { class: 'cms-shell' },
    el(
      'header',
      { class: 'cms-header' },
      el(
        'div',
        { class: 'cms-brand' },
        el('div', { class: `cms-brand__logo cms-brand__logo--${loadLang()}`, attrs: { role: 'img', 'aria-label': loadLang() === 'zh' ? '可口可樂回收學院' : 'Coca-Cola Recycling Academy' } },
          el('img', { attrs: { src: whiteLogoSheet, alt: '', 'aria-hidden': 'true' } }))
      ),
      el(
        'div',
        { class: 'cms-account' },
        el('span', {}, el('strong', { text: session.user.displayName }), el('small', { text: isAdmin ? 'System administrator' : 'Teacher' })),
        el(
          'div',
          { class: 'cms-account-actions' },
          el('button', {
            class: 'cms-play-games',
            text: 'Play games',
            attrs: { type: 'button' },
            on: { click: playGames },
          }),
          el('button', {
            class: 'cms-signout',
            text: 'Sign out',
            attrs: { type: 'button' },
            on: { click: () => void auth.signOut().finally(() => window.location.reload()) },
          })
        )
      )
    ),
    el('div', { class: 'cms-command-bar' }, nav, ...(isAdmin ? [schoolSelect] : [])),
    el('div', { class: 'cms-status-row' }, status),
    viewRoot
  );

  root.replaceChildren(shell);
  document.documentElement.setAttribute('data-route', 'cms');
  const requestedView = window.location.hash.match(/^#\/cms\/(dashboard|students|teachers|schools|admins|audit)$/)?.[1] as CmsViewId | undefined;
  if (requestedView && navItems.some((item) => item.id === requestedView)) activeView = requestedView;

  if (isAdmin) {
    try {
      schools = await client.schools();
      updateSchoolSelect();
      if (!schoolId && activeView !== 'admins' && activeView !== 'audit') activeView = 'schools';
    } catch (reason) {
      announce(errorMessage(reason), true);
    }
  }
  await renderView();
}
